// Floating tab bar contract with the Sotravel app (sotravel-expo
// src/components/tabBarOverlay.ts). In a webview that sits under the app's
// floating tab bar, the app sets `window.__SOTRAVEL_TABBAR_OVERLAY__` and two
// CSS variables on <html>:
//   --app-tabbar-inset  room the bar takes at the bottom (0 when it's hidden)
//   --app-safe-bottom   the home indicator / gesture area
// and fires a `sotravel-tabbar` event when they change. On the website and in
// older app versions none of this exists, so everything defaults to 0px.
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

export const isTabBarOverlay = () =>
  typeof window !== "undefined" && !!window.__SOTRAVEL_TABBAR_OVERLAY__;

// Re-renders when the app (re)applies the contract, e.g. after page load.
export const useTabBarOverlay = () => {
  const [overlay, setOverlay] = useState(isTabBarOverlay);
  useEffect(() => {
    const update = () => setOverlay(isTabBarOverlay());
    window.addEventListener("sotravel-tabbar", update);
    update();
    return () => window.removeEventListener("sotravel-tabbar", update);
  }, []);
  return overlay;
};

// Tell the app how tall the page's bottom banner is (0 = gone) so the bar
// floats just above it, and let pages pad for it via --app-bottom-banner.
// Only under the app's bar: on the website and in older app versions nothing
// changes (the variable stays unset).
export const reportBottomBanner = (height) => {
  if (!isTabBarOverlay()) return;
  const h = Math.max(0, Math.round(height || 0));
  document.documentElement.style.setProperty("--app-bottom-banner", `${h}px`);
  window.ReactNativeWebView?.postMessage?.(JSON.stringify({ type: "bottomBanner", height: h }));
};

// Pages get room at their end for the bar (and the banner under it). The club
// dashboard lays out its own bottom spacing, so it's left alone here.
const SELF_PADDED = /^\/club\/(manage|creation)(\/|$)/;

export const AppTabBarSpacing = () => {
  const { pathname } = useLocation();
  const overlay = useTabBarOverlay();
  useEffect(() => {
    document.documentElement.classList.toggle("app-tabbar-pad", overlay && !SELF_PADDED.test(pathname));
  }, [overlay, pathname]);
  return null;
};
